import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { formatarData, type ClienteAdmin } from '@/lib/plataforma_admin';
import {
  Blueprint,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

interface ClientesSectionProps {
  clientes: ClienteAdmin[] | null;
  erro: string | null;
  salvando: boolean;
  // `unknown` de propósito: a página devolve o boolean de sucesso do
  // `executar`, e esta seção só aguarda a conclusão.
  onCriar: (nome: string, cnpj: string | null) => Promise<unknown>;
}

const _SO_NUMEROS = /^\d{14}$/;

/**
 * Lista de clientes + cadastro do cliente novo (E53.F5). // ux-neutrality:allow doc-comment
 *
 * A validação daqui é conforto (evita ida e volta); a régua que vale é a do // ux-neutrality:allow doc-comment
 * servidor, e a mensagem de recusa dele é a que a tela mostra. // ux-neutrality:allow doc-comment
 */
export function ClientesSection({ clientes, erro, salvando, onCriar }: ClientesSectionProps) {
  const [nome, setNome] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [validacao, setValidacao] = useState<string | null>(null);

  async function handleSubmit(evento: React.FormEvent) {
    evento.preventDefault();
    const nomeLimpo = nome.trim();
    if (nomeLimpo === '') {
      setValidacao(ptBR.admin.validationNomeObrigatorio);
      return;
    }
    const cnpjLimpo = cnpj.trim();
    if (cnpjLimpo !== '' && !_SO_NUMEROS.test(cnpjLimpo)) {
      setValidacao(ptBR.admin.validationDocumento);
      return;
    }
    setValidacao(null);
    await onCriar(nomeLimpo, cnpjLimpo === '' ? null : cnpjLimpo);
    setNome('');
    setCnpj('');
  }

  return (
    <section className="flex flex-col gap-3" data-testid="admin-clientes">
      <h2 className="text-heading-sm text-fg">{ptBR.admin.clientesSectionTitle}</h2>

      {erro !== null ? (
        <p data-testid="clientes-erro" className="text-body-md text-danger-700">
          {erro}
        </p>
      ) : clientes === null ? (
        <p className="text-body-md text-fg-muted">{ptBR.admin.escritoriosCarregando}</p>
      ) : clientes.length === 0 ? (
        <p data-testid="clientes-vazio" className="text-body-md text-fg-muted">
          {ptBR.admin.clientesVazio}
        </p>
      ) : (
        <Blueprint className="min-w-0">
          {/* E54.F11.C02: `contain: paint` fecha o vazamento da largura
              intrínseca da tabela. Só `overflow-x: auto` não basta — a largura
              de conteúdo de um `display: table` atravessa o contêiner de
              rolagem e reaparece como estouro da coluna, e a varredura a 390px
              acusa a tela inteira rolando. */}
          <div className="min-w-0 overflow-x-auto [contain:layout]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{ptBR.admin.clienteColNome}</TableHead>
                  <TableHead>{ptBR.admin.clienteColDocumento}</TableHead>
                  <TableHead>{ptBR.admin.clienteColEscritorios}</TableHead>
                  <TableHead>{ptBR.admin.clienteColCriadoEm}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientes.map((cliente) => (
                  <TableRow key={cliente.id} data-testid="cliente-linha">
                    <TableCell className="text-fg">{cliente.nome}</TableCell>
                    <TableCell className="font-mono text-body-sm text-fg-muted">
                      {cliente.cnpj ?? ptBR.admin.valorAusente}
                    </TableCell>
                    <TableCell className="text-fg-muted">{cliente.total_escritorios}</TableCell>
                    <TableCell className="text-fg-muted">
                      {formatarData(cliente.criado_em)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Blueprint>
      )}

      <form
        onSubmit={(evento) => void handleSubmit(evento)}
        className="flex flex-col gap-sc-2 p-sc-3"
        data-testid="novo-cliente-form"
      >
        <h3 className="text-body-md font-medium text-fg">{ptBR.admin.novoClienteTitle}</h3>
        <div className="flex flex-wrap gap-2">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoClienteNome}</span>
            <Input
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              data-testid="novo-cliente-nome"
            />
          </label>
          <label className="flex min-w-[180px] flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoClienteDocumento}</span>
            <Input
              value={cnpj}
              onChange={(evento) => setCnpj(evento.target.value)}
              inputMode="numeric"
              data-testid="novo-cliente-cnpj"
            />
          </label>
        </div>
        {validacao !== null ? (
          <p
            role="alert"
            data-testid="novo-cliente-validacao"
            className="text-body-sm text-danger-700"
          >
            {validacao}
          </p>
        ) : null}
        <div>
          <Button
            variant="secondary"
            type="submit"
            disabled={salvando}
            data-testid="novo-cliente-submit"
          >
            {salvando ? ptBR.admin.novoClienteEnviando : ptBR.admin.novoClienteSubmit}
          </Button>
        </div>
      </form>
    </section>
  );
}
